import {
  Add,
  ContentCopy,
  CopyAll,
  FormatListBulleted,
  GridView,
  ModeEditOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import {
  FieldArray,
  FieldArrayRenderProps,
  useField,
  useFormikContext,
} from "formik";
import React, { useState } from "react";

import { SelectOption } from "components/form/SelectOption";
import { TextFieldString } from "components/form/TextFieldString";
import LookupAddress from "components/form_v2/LookupAddress";
import { SelectUSState } from "components/form_v2/SelectUSState";
import {
  CompanyLookupField,
  ContactLookupField,
} from "pages/deal/components/LookupField";
import { parseRecordAsSimpleOption } from "pages/deal/utils/deal_form";
import { Company, CompanyRead } from "types/api/deal/company";
import { ContactRead } from "types/api/deal/contact";
import { LookupItem } from "types/api/deal/form";
import { PartyRoleEnum } from "types/api/deal/transaction_party";
import { AddressSearchResult } from "types/form/dealForm";
import { dealService } from "utils/axios";

interface TransactionPartyFormValues {
  id?: string | null;
  role: PartyRoleEnum;

  contact?: LookupItem | null;
  company?: LookupItem | null;

  first_name?: string | null;
  last_name?: string | null;
  email_address?: string | null;

  address_line_1?: null | string;
  address_line_2?: null | string;
  address_state?: null | string;
  address_city?: null | string;
  address_postal_code?: null | string;
  address_country?: null | string;
  address_latitude?: null | number;
  address_longitude?: null | number;
}

export const PARTY_ROLE_LOOKUP_ITEMS = [
  { key: PartyRoleEnum.BILL_TO, label: "Bill To" },
  { key: PartyRoleEnum.OWNER, label: "Owner" },
  { key: PartyRoleEnum.TENANT, label: "Tenant" },
  { key: PartyRoleEnum.BUYER, label: "Buyer" },
  { key: PartyRoleEnum.SELLER, label: "Seller" },
  { key: PartyRoleEnum.OTHER, label: "Other" },
];

const getEmptyTransactionPartyRow = () =>
  ({
    role: PartyRoleEnum.OTHER,
    first_name: "",
    last_name: "",
    email_address: "",
    contact: null,
    company: null,
    address_line_1: "",
    address_line_2: "",
    address_city: "",
    address_state: "",
    address_postal_code: "",
  }) as TransactionPartyFormValues;

export const TransactionPartiesField = () => {
  const { setFieldValue } = useFormikContext();
  const [transactionPartyField] = useField("transaction_parties");

  // Track which party row (if any) is being edited
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);

  const onContactFieldChange = async (value: any) => {
    // Drop out early if no value
    if (!value || !value.key) return;

    try {
      const contactObj: ContactRead | undefined = await dealService
        .get(`/contact/${value.key}`)
        .then((response) => {
          return response?.data?.data || undefined;
        });

      if (!contactObj) return;

      // Define fields to check and set dynamically
      const contactFieldValues: Record<string, any> = {
        company: parseRecordAsSimpleOption<Company>(contactObj.company, "name"),
        first_name: contactObj.first_name,
        last_name: contactObj.last_name,
        email_address: contactObj.email_address,
        address_line_1: contactObj.company?.line_1,
        address_line_2: contactObj.company?.line_2,
        address_city: contactObj.company?.city,
        address_state: contactObj.company?.state,
        address_postal_code: contactObj.company?.postal_code,
        address_country: contactObj.company?.country,
      };

      // Set only if values[field] is empty
      Object.entries(contactFieldValues).forEach(([formField, fieldValue]) => {
        setFieldValue(
          `transaction_parties.${editingIndex}.${formField}`,
          fieldValue
        );
      });
    } catch (error) {
      console.error("Error fetching contact details:", error);
    }
  };

  const onCompanyFieldChange = async (value: any) => {
    // Drop out early if no value
    if (!value || !value.key) return;

    try {
      const companyObj: CompanyRead | undefined = await dealService
        .get(`/company/${value.key}`)
        .then((response) => {
          return response?.data?.data || undefined;
        });

      if (!companyObj) return;

      // Define fields to check and set dynamically
      const contactFieldValues: Record<string, any> = {
        company: parseRecordAsSimpleOption<CompanyRead>(companyObj, "name"),
        address_line_1: companyObj.line_1,
        address_line_2: companyObj.line_2,
        address_city: companyObj.city,
        address_state: companyObj.state,
        address_postal_code: companyObj.postal_code,
        address_country: companyObj.country,
      };

      // Set only if values[field] is empty
      Object.entries(contactFieldValues).forEach(([formField, fieldValue]) => {
        setFieldValue(
          `transaction_parties.${editingIndex}.${formField}`,
          fieldValue
        );
      });
    } catch (error) {
      console.error("Error fetching company details:", error);
    }
  };

  const onAddressSearchChange = (address: AddressSearchResult) => {
    const addressFieldValues = {
      address_line_1: address.line_1,
      address_line_2: address.line_2,
      address_city: address.city,
      address_state: address.state,
      address_postal_code: address.postal_code,
      address_country: address.country,
    };

    Object.entries(addressFieldValues).forEach(([formField, fieldValue]) => {
      setFieldValue(
        `transaction_parties.${editingIndex}.${formField}`,
        fieldValue
      );
    });
  };

  const [isGridView, setIsGridView] = useState(true);

  const [copied, setCopied] = useState(false);

  const handleCopy = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy!", err);
    }
  };

  return (
    <FieldArray name="transaction_parties">
      {(arrayHelpers: FieldArrayRenderProps) => {
        const parties: TransactionPartyFormValues[] =
          transactionPartyField.value || [];

        if (parties.length === 0) {
          return (
            <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
              <Typography>
                No transaction parties have been added yet.
              </Typography>
              <Box mt={2}>
                <Button
                  variant="contained"
                  onClick={() =>
                    arrayHelpers.push(getEmptyTransactionPartyRow())
                  }
                >
                  Add Your First Party
                </Button>
              </Box>
            </Paper>
          );
        }

        return (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: "24px",
              }}
            >
              <ToggleButtonGroup
                color="primary"
                value={isGridView}
                exclusive
                onChange={() => setIsGridView(!isGridView)}
              >
                <ToggleButton size="small" value={true}>
                  <GridView />
                </ToggleButton>
                <ToggleButton size="small" value={false}>
                  <FormatListBulleted />
                </ToggleButton>
              </ToggleButtonGroup>
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  onClick={() =>
                    arrayHelpers.push(getEmptyTransactionPartyRow())
                  }
                  variant="outlined"
                  size="large"
                  startIcon={<Add />}
                >
                  Add Contact
                </Button>
              </Stack>
            </Box>
            {parties.length > 0 && !isGridView ? (
              <TableContainer
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Table
                  sx={{
                    border: 1,
                    borderColor: "#E0E0E0",
                    borderRadius: 2,
                    padding: 10,
                    overflow: "hidden",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Role</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parties.map((party, index) => {
                      // Role label
                      const roleLabel =
                        PARTY_ROLE_LOOKUP_ITEMS.find(
                          (r) => r.key === party.role
                        )?.label || party.role;

                      // Name display
                      const companyName = party.company?.label || "";
                      const contactName = [party.first_name, party.last_name]
                        .filter(Boolean)
                        .join(" ");
                      const nameDisplay = [companyName, contactName]
                        .filter(Boolean)
                        .join(" - ");

                      // Address display
                      const addressDisplay = [
                        party.address_line_1,
                        party.address_line_2,
                        party.address_city,
                        party.address_state,
                        party.address_postal_code,
                      ]
                        .filter(Boolean)
                        .join(", ");

                      return (
                        <TableRow key={party.id ?? index}>
                          <TableCell>{roleLabel}</TableCell>
                          <TableCell>{nameDisplay || "—"}</TableCell>
                          <TableCell>{addressDisplay || "—"}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => setEditingIndex(index)}
                              aria-label="edit"
                            >
                              <ModeEditOutlined color="primary" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Grid container spacing={2}>
                {parties.map((party, index) => {
                  return (
                    <Grid item xs={12} sm={6} key={party.id ?? index}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.25,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="h5" sx={{ flex: 1 }}>
                              {party.first_name} {party.last_name}
                            </Typography>
                            {!!party.role && party.role !== "other" && (
                              <Chip
                                label={party.role}
                                sx={{
                                  borderRadius: "16px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  textTransform: "capitalize",
                                  height: "24px",
                                  color: "#25557D",
                                  backgroundColor: "#E6F0FF",
                                }}
                              />
                            )}
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => setEditingIndex(index)}
                            aria-label="edit"
                          >
                            <ModeEditOutlined color="primary" />
                          </IconButton>
                        </Box>
                        {party.company && (
                          <Typography variant="subtitle2" fontWeight={600}>
                            {party.company?.label}
                          </Typography>
                        )}
                        <Typography variant="subtitle2" fontWeight={600}>
                          {party.address_line_1} {party.address_line_2}{" "}
                          {party.address_city} {party.address_state}{" "}
                          {party.address_postal_code}
                        </Typography>
                        <Box>
                          <Link
                            href={`mailto:${party.email_address}`}
                            target="_blank"
                            sx={{ fontWeight: 600, fontSize: "12px" }}
                          >
                            {party.email_address}
                          </Link>
                          <Tooltip title={copied ? "Copied!" : "Copy"}>
                            <IconButton
                              onClick={() =>
                                party.email_address &&
                                handleCopy(party.email_address)
                              }
                              color="primary"
                            >
                              <CopyAll sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
            {/* Edit Dialog */}
            <Dialog
              open={editingIndex !== null}
              onClose={() => setEditingIndex(null)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>
                <Stack
                  direction={"row"}
                  spacing={2}
                  alignItems="center"
                  justifyContent={"space-between"}
                >
                  <Typography variant="h5">Edit Transaction Party</Typography>
                  <Box sx={{ width: 200 }}>
                    <SelectOption
                      displayName="Role"
                      fieldName={`transaction_parties.${editingIndex}.role`}
                      options={PARTY_ROLE_LOOKUP_ITEMS}
                    />
                  </Box>
                </Stack>
              </DialogTitle>
              <DialogContent>
                {editingIndex !== null && (
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <ContactLookupField
                        fieldName={`transaction_parties.${editingIndex}.contact`}
                        displayName="Contact"
                        additionalChangeHandler={onContactFieldChange}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CompanyLookupField
                        fieldName={`transaction_parties.${editingIndex}.company`}
                        displayName="Company"
                        additionalChangeHandler={onCompanyFieldChange}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextFieldString
                        fieldName={`transaction_parties.${editingIndex}.first_name`}
                        displayName="First Name"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextFieldString
                        fieldName={`transaction_parties.${editingIndex}.last_name`}
                        displayName="Last Name"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <LookupAddress
                        onValueChange={onAddressSearchChange}
                        fieldName={`transaction_parties.${editingIndex}._address_search`}
                        displayName={"Address Search"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextFieldString
                        fieldName={`transaction_parties.${editingIndex}.address_line_1`}
                        displayName={"Street address"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextFieldString
                        fieldName={`transaction_parties.${editingIndex}.address_line_2`}
                        displayName={"Apt / suite"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextFieldString
                        fieldName={`transaction_parties.${editingIndex}.address_city`}
                        displayName={"City"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <SelectUSState
                        fieldName={`transaction_parties.${editingIndex}.address_state`}
                        displayName={"State"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextFieldString
                        fieldName={`transaction_parties.${editingIndex}.address_postal_code`}
                        displayName={"Zip"}
                      />
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setEditingIndex(null)}>OK</Button>
              </DialogActions>
            </Dialog>
          </Box>
        );
      }}
    </FieldArray>
  );
};
